import { FormatInputError, numberFormat } from "./errors";
import { assertCurrency, currencyMinorDigits, minorToDecimalString, type MoneyFormatOptions } from "./money";

// Money contract 2 values (contracts/currency/money_v2.schema.json): amounts
// are decimal strings in ISO minor units with the exponent sent by the
// server, rates are exact major-unit decimal strings. Nothing here goes
// through a binary float, rounds, converts currencies or knows which
// currencies are payable: the currency comes from the record, the exponent
// from the server, and registry membership (MV-2) is checked server-side.
//
// This is a different scale from the legacy fields: `*_cents` are fixed
// hundredths whatever the currency (moneyHundredths), so "1375" JPY with
// exponent 0 is JPY 1,375 here and never JPY 13.75.
//
// Output shows the ISO code by default ("USD 2.38", never a bare "$" that
// could be CAD); display: "symbol" is for a consumer whose own context
// already names the currency.

export type RateUnit = "kWh" | "hour" | "session";
export type RatePrecisionOrigin = "new_input" | "legacy_snapshot";

export const RATE_UNITS: readonly RateUnit[] = ["kWh", "hour", "session"];

/** Non-negative final amount: charges, totals, holds, captured, outstanding. */
export interface MoneyV2 {
  readonly currency: string;
  /** Canonical decimal integer string, 0..INT64_MAX. */
  readonly amount_minor: string;
  readonly exponent: number;
  readonly money_version: 2;
}

/** Signed amount of an adjustment role (discount, continuation suppression, correction, capture adjustment). */
export interface SignedMoneyV2 {
  readonly currency: string;
  /** Canonical signed decimal integer string, INT64_MIN..INT64_MAX; never "-0". */
  readonly amount_minor: string;
  readonly exponent: number;
  readonly money_version: 2;
}

/** Price per unit; never final money and never rounded to the currency exponent. */
export interface RateV2 {
  readonly currency: string;
  /** Canonical exact major-unit decimal: "0.125", "0.00000000000000000001". */
  readonly amount: string;
  /** Contract token for a catalog label, not display text. */
  readonly unit: RateUnit;
  readonly precision_origin: RatePrecisionOrigin;
  readonly money_version: 2;
}

/** Sent instead of a rate whose frozen legacy value has no finite decimal; raw is the stored text. */
export interface LegacyRateUnrepresentable {
  readonly diagnostic: "legacy_rate_unrepresentable";
  readonly raw: string;
  readonly unit: RateUnit;
  readonly money_version: 2;
}

export type MoneyV2ErrorCode =
  | "not_object"
  | "missing_field"
  | "money_version"
  | "currency"
  | "exponent"
  | "amount"
  | "out_of_range"
  | "unit"
  | "precision_origin"
  | "precision"
  | "raw";

/**
 * null or an absent field is "unavailable" (unknown, never a zero and never
 * USD); a value that breaks the contract is "invalid" and is not shown as money.
 */
export type MoneyV2Read<T> =
  | { readonly status: "value"; readonly value: T }
  | { readonly status: "unavailable" }
  | { readonly status: "invalid"; readonly error: MoneyV2ErrorCode; readonly detail: string };

const CURRENCY = /^[A-Z]{3}$/;
const MINOR = /^(0|[1-9][0-9]{0,18})$/;
const SIGNED_MINOR = /^(0|-?[1-9][0-9]{0,18})$/;
const RATE = /^(0|[1-9][0-9]*)(\.[0-9]*[1-9])?$/;
const RATE_NEW_INPUT = /^(0|[1-9][0-9]{0,8})(\.[0-9]{0,5}[1-9])?$/;
const INT64_MAX = 9223372036854775807n;
const INT64_MIN = -9223372036854775808n;
const MAX_EXPONENT = 4;

const MONEY_FIELDS = ["currency", "amount_minor", "exponent", "money_version"];
const RATE_FIELDS = ["currency", "amount", "unit", "precision_origin", "money_version"];
const DIAGNOSTIC_FIELDS = ["diagnostic", "raw", "unit", "money_version"];

class Invalid extends Error {
  readonly error: MoneyV2ErrorCode;
  readonly detail: string;

  constructor(error: MoneyV2ErrorCode, detail: string) {
    super(`${error}: ${detail}`);
    this.error = error;
    this.detail = detail;
  }
}

const show = (value: unknown) => (typeof value === "string" ? JSON.stringify(value) : typeof value === "bigint" ? `${value}n` : String(value));

// Reads additively: every canonical field must be present and valid, and
// only those are taken. Other keys (future metadata, or a legacy float
// such as total_cost) are ignored and can neither invalidate nor replace
// amount_minor. Closing value objects is the server's rule when writing.
function fields(value: unknown, required: readonly string[]): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Invalid("not_object", show(value));
  const record = value as Record<string, unknown>;
  const missing = required.find((key) => !Object.prototype.hasOwnProperty.call(record, key));
  if (missing !== undefined) throw new Invalid("missing_field", missing);
  if (record.money_version !== 2) throw new Invalid("money_version", show(record.money_version));
  return Object.fromEntries(required.map((key) => [key, record[key]]));
}

function currency(value: unknown): string {
  if (typeof value !== "string" || !CURRENCY.test(value)) throw new Invalid("currency", show(value));
  return value;
}

function exponent(value: unknown): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > MAX_EXPONENT) throw new Invalid("exponent", show(value));
  return value;
}

function amountMinor(value: unknown, signed: boolean): string {
  if (typeof value !== "string" || !(signed ? SIGNED_MINOR : MINOR).test(value)) throw new Invalid("amount", show(value));
  const minor = BigInt(value);
  if (minor > INT64_MAX || minor < INT64_MIN) throw new Invalid("out_of_range", value);
  return value;
}

function unit(value: unknown): RateUnit {
  if (!RATE_UNITS.includes(value as RateUnit)) throw new Invalid("unit", show(value));
  return value as RateUnit;
}

function read<T>(value: unknown, parse: (value: unknown) => T): MoneyV2Read<T> {
  if (value === null || value === undefined) return { status: "unavailable" };
  try {
    return { status: "value", value: Object.freeze(parse(value)) };
  } catch (e) {
    if (e instanceof Invalid) return { status: "invalid", error: e.error, detail: e.detail };
    throw e;
  }
}

function money(value: unknown, signed: boolean): MoneyV2 {
  const record = fields(value, MONEY_FIELDS);
  return { currency: currency(record.currency), amount_minor: amountMinor(record.amount_minor, signed), exponent: exponent(record.exponent), money_version: 2 };
}

/** Reads a contract `money` (or `money_or_unknown`) value: amount_minor >= 0. */
export function readMoneyV2(value: unknown): MoneyV2Read<MoneyV2> {
  return read(value, (v) => money(v, false));
}

/** Reads a contract `signed_money` value; only adjustment roles carry one. */
export function readSignedMoneyV2(value: unknown): MoneyV2Read<SignedMoneyV2> {
  return read(value, (v) => money(v, true));
}

function rate(value: unknown): RateV2 | LegacyRateUnrepresentable {
  if (typeof value === "object" && value !== null && "diagnostic" in value) {
    const record = fields(value, DIAGNOSTIC_FIELDS);
    if (record.diagnostic !== "legacy_rate_unrepresentable") throw new Invalid("not_object", `diagnostic ${show(record.diagnostic)}`);
    if (typeof record.raw !== "string" || record.raw === "") throw new Invalid("raw", show(record.raw));
    return { diagnostic: "legacy_rate_unrepresentable", raw: record.raw, unit: unit(record.unit), money_version: 2 };
  }
  const record = fields(value, RATE_FIELDS);
  const origin = record.precision_origin;
  if (origin !== "new_input" && origin !== "legacy_snapshot") throw new Invalid("precision_origin", show(origin));
  if (typeof record.amount !== "string" || !RATE.test(record.amount)) throw new Invalid("amount", show(record.amount));
  // A legacy snapshot keeps every stored digit; new operator input is capped at 9 + 6 digits.
  if (origin === "new_input" && !RATE_NEW_INPUT.test(record.amount)) throw new Invalid("precision", record.amount);
  return { currency: currency(record.currency), amount: record.amount, unit: unit(record.unit), precision_origin: origin, money_version: 2 };
}

/** Reads a contract `rate`, or the `legacy_rate_unrepresentable` diagnostic sent in its place. */
export function readRateV2(value: unknown): MoneyV2Read<RateV2 | LegacyRateUnrepresentable> {
  return read(value, rate);
}

export const isLegacyRateUnrepresentable = (value: RateV2 | LegacyRateUnrepresentable): value is LegacyRateUnrepresentable => "diagnostic" in value;

function valid<T>(result: MoneyV2Read<T>): T {
  if (result.status === "value") return result.value;
  throw new FormatInputError("decimal", result.status === "invalid" ? `${result.error}: ${result.detail}` : "money is unavailable");
}

/** Exact major-unit decimal of a v2 amount with its own exponent: ("-125", 2) -> "-1.25". */
export function moneyV2Decimal(value: MoneyV2 | SignedMoneyV2): string {
  const amount = valid(readSignedMoneyV2(value));
  return minorToDecimalString(BigInt(amount.amount_minor), amount.exponent);
}

function groupSymbol(format: Intl.NumberFormat): string {
  return format.formatToParts(1000).find((part) => part.type === "group")?.value ?? "";
}

/**
 * Integer and group parts of a non-negative integer. Intl formats a bigint
 * exactly; an engine that coerces it to a float, or lacks bigint support,
 * fails the digit check and gets the format's own group symbol every three
 * digits instead (the en-US pattern), so no digit is ever lost.
 */
function integerParts(format: Intl.NumberFormat, whole: string): string {
  try {
    const parts = format.formatToParts(BigInt(whole)).filter((part) => part.type === "integer" || part.type === "group");
    const digits = parts.filter((part) => part.type === "integer").map((part) => part.value);
    if (digits.join("") === whole) return parts.map((part) => part.value).join("");
  } catch (e) {
    if (!(e instanceof TypeError || e instanceof RangeError)) throw e;
  }
  const group = groupSymbol(format);
  const head = whole.length % 3 || 3;
  const groups = [whole.slice(0, head)];
  for (let i = head; i < whole.length; i += 3) groups.push(whole.slice(i, i + 3));
  return groups.join(group);
}

/**
 * Formats a canonical decimal string in a currency without any rounding and
 * without passing it to Intl as a number or a string: Intl lays out sign,
 * symbol and separators for a template of the same shape, the digits are
 * the input's own. The fraction keeps every digit (beyond Intl's cap of 20
 * or 100), padded with zeros to minFraction.
 */
function formatExactDecimal(decimal: string, currencyCode: string, minFraction: number, display: "symbol" | "code"): string {
  assertCurrency(currencyCode);
  const negative = decimal.startsWith("-");
  const [whole, fraction = ""] = (negative ? decimal.slice(1) : decimal).split(".");
  const digits = fraction.padEnd(minFraction, "0");
  const templateDigits = digits ? 1 : 0;
  const format = numberFormat({ style: "currency", currency: currencyCode, currencyDisplay: display, minimumFractionDigits: templateDigits, maximumFractionDigits: templateDigits, signDisplay: "negative" });
  const template = format.formatToParts(negative ? -1 : 1);
  return template.map((part) => (part.type === "integer" ? integerParts(format, whole) : part.type === "fraction" ? digits : part.value)).join("");
}

/** A v2 amount with exactly its server exponent: {USD, "9007199254740993", 2} -> "USD 90,071,992,547,409.93". */
export function formatMoneyV2(value: MoneyV2 | SignedMoneyV2, options: Pick<MoneyFormatOptions, "display"> = {}): string {
  const amount = valid(readSignedMoneyV2(value));
  return formatExactDecimal(moneyV2Decimal(amount), amount.currency, amount.exponent, options.display ?? "code");
}

export interface RateFormatOptions extends Pick<MoneyFormatOptions, "display"> {
  /**
   * Zero padding only, never rounding. Pass the exponent of the record the
   * rate belongs to; without it the currency's ISO digits from Intl are used.
   */
  readonly minimumFractionDigits?: number;
}

/** A rate amount with every exact digit: 0.125 EUR -> "EUR 0.125", legacy 1e-20 -> "USD 0.00000000000000000001". Pair it with a catalog label for rate.unit. */
export function formatRateV2(value: RateV2, options: RateFormatOptions = {}): string {
  const parsed = valid(readRateV2(value));
  if (isLegacyRateUnrepresentable(parsed)) throw new FormatInputError("decimal", `legacy rate ${JSON.stringify(parsed.raw)} is unrepresentable`);
  const minFraction = options.minimumFractionDigits ?? currencyMinorDigits(parsed.currency);
  if (!Number.isInteger(minFraction) || minFraction < 0 || minFraction > MAX_EXPONENT) throw new FormatInputError("decimal", `minimumFractionDigits ${minFraction}`);
  return formatExactDecimal(parsed.amount, parsed.currency, minFraction, options.display ?? "code");
}

function attempt(format: () => string): string | null {
  try {
    return format();
  } catch (e) {
    if (e instanceof FormatInputError) return null;
    throw e;
  }
}

/** Display text of an API `money` value; null when unknown, invalid or in a currency Intl cannot show. */
export function moneyV2Display(value: unknown, options: Pick<MoneyFormatOptions, "display"> = {}): string | null {
  const result = readMoneyV2(value);
  return result.status === "value" ? attempt(() => formatMoneyV2(result.value, options)) : null;
}

/** Display text of an API `signed_money` value; null when unknown, invalid or in a currency Intl cannot show. */
export function signedMoneyV2Display(value: unknown, options: Pick<MoneyFormatOptions, "display"> = {}): string | null {
  const result = readSignedMoneyV2(value);
  return result.status === "value" ? attempt(() => formatMoneyV2(result.value, options)) : null;
}

/**
 * Amount text and unit token of an API rate, for "{amount} per {unit}" catalog
 * messages; null when unknown, invalid, unrepresentable or not displayable.
 */
export function rateV2Display(value: unknown, options: RateFormatOptions = {}): { readonly amount: string; readonly unit: RateUnit } | null {
  const result = readRateV2(value);
  if (result.status !== "value" || isLegacyRateUnrepresentable(result.value)) return null;
  const parsed = result.value;
  const amount = attempt(() => formatRateV2(parsed, options));
  return amount === null ? null : { amount, unit: parsed.unit };
}
